import { createContext, useContext, useState } from "react";

// displayName, emailVerified and uid came from the Firebase user object and
// were read nowhere, so they left with it. isBusinessAdmin moved in here
// because GET /auth/me returns both roles in one answer, which retires the
// separate lookup useBusinessAdminStatus used to make on every mount.
export interface User {
	email: string;
	isAdmin: boolean;
	isBusinessAdmin: boolean;
}

interface AuthContextValue {
	user: User | null;
	setUser: (user: User) => void;
	clearUser: () => void;
}

// Holds the signed-in user. This used to be a Redux store whose entire state
// was this one object, which cost @reduxjs/toolkit + immer + react-redux +
// redux in the entry bundle for a value that changes only on sign in and sign
// out. A context is enough.
const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthStateProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [user, setUser] = useState<User | null>(null);

	const value = {
		user,
		setUser,
		clearUser: () => setUser(null),
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used inside AuthStateProvider");
	}
	return context;
};

// Most call sites only read the user, so this keeps them from destructuring.
export const useAuthUser = () => useAuth().user;
